// src/pages/Dashboard.jsx
import { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import BottomBar from "../components/BottomBar";
import ThaiBEDateRangePicker from "../components/ThaiBEDateRangePicker";
import { ReactComponent as CalendarIcon } from "../assets/images/calendar.svg";
import { ReactComponent as Card1Icon } from "../assets/images/card1.svg";
import { ReactComponent as Card2Icon } from "../assets/images/card2.svg";
import { ReactComponent as Card3Icon } from "../assets/images/card3.svg";
import { ReactComponent as Card4Icon } from "../assets/images/card4.svg";
import { createHttpClient } from "../api/client";

import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";

const THB = new Intl.NumberFormat("th-TH", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const THB_NO_DECIMALS = new Intl.NumberFormat("th-TH", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function pad2(n) {
  return String(n).padStart(2, "0");
}

// YYYY-MM-DDTHH:mm:ss (ไม่มี Z)
function toLocalISO(date, h = 0, m = 0, s = 0) {
  if (!date) return "";
  const d = new Date(date);
  const yy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  return `${yy}-${mm}-${dd}T${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

function readStoredProfile() {
  const keys = ["profile", "PROFILE", "user_profile", "USER_PROFILE"];
  for (const k of keys) {
    const raw = localStorage.getItem(k);
    if (!raw) continue;
    try {
      const obj = JSON.parse(raw);
      if (obj?.user) return obj;
    } catch { }
  }
  return null;
}

function LoadingOverlay({ show }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[999] bg-white/70 backdrop-blur-[2px] grid place-items-center">
      <div className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 px-5 py-4 shadow">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
        <div className="text-[14px] text-slate-700">กำลังโหลดข้อมูล…</div>
      </div>
    </div>
  );
}

// ✅ สี Top5 ตามรูป
const TOP5_COLORS = ["#FF3B60", "#CD3AE1", "#6A5FF5", "#1190FF", "#0DC3E9"];

export default function Dashboard() {
  const http = useMemo(() => createHttpClient(), []);
  const today = useMemo(() => new Date(), []);

  const [range, setRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
  });

  // ✅ rows = เอามาจาก data.all_foodtype
  const [rows, setRows] = useState([]); // [{ name, value, color }]

  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [kpi, setKpi] = useState({
    totalKg: 0,
    totalMeals: 0,
    totalBeneficiaries: 0,
    donors: 0,
    donateTimes: 0,
  });

  const PAGE_SIZE = 8;
  const [page, setPage] = useState(1);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((rows?.length || 0) / PAGE_SIZE));
  }, [rows]);

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return (rows || []).slice(start, start + PAGE_SIZE);
  }, [rows, page]);

  // ✅ pie: top5 + อื่นๆ
  const pieData = useMemo(() => {
    const arr = Array.isArray(rows) ? [...rows] : [];
    arr.sort((a, b) => Number(b.value || 0) - Number(a.value || 0));

    const top = arr.slice(0, 5);
    const rest = arr.slice(5);
    const restSum = rest.reduce((s, x) => s + Number(x.value || 0), 0);

    const out = [...top];
    if (restSum > 0) out.push({ name: "อื่นๆ", value: restSum, color: "#CBD5E1" });
    return out;
  }, [rows]);

  const pieTotal = useMemo(() => {
    return pieData.reduce((s, x) => s + Number(x.value || 0), 0) || 1;
  }, [pieData]);

  const handleDownloadWeeklyExcel = async () => {
    try {
      setIsDownloading(true);

      const response = await http.get("/api/export/users/udonthani/weekly", {
        responseType: "blob",
      });

      const contentDisposition = response?.headers?.["content-disposition"] || "";
      const encodedFileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      const plainFileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

      const fileName = encodedFileNameMatch?.[1]
        ? decodeURIComponent(encodedFileNameMatch[1])
        : plainFileNameMatch?.[1] || `users-udonthani-weekly-${toLocalISO(new Date()).slice(0, 10)}.xlsx`;

      const blob = new Blob([
        response.data,
      ], {
        type: response?.headers?.["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("GET /api/export/users/udonthani/weekly error:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  // ✅ fetch dashboard ตามช่วงวันที่
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        // setLoading(true);

        const cached = readStoredProfile();
        const userId = Number(cached?.user?.id || 19);

        const start_date = toLocalISO(range.startDate, 0, 0, 0);
        const end_date = range.endDate ? toLocalISO(range.endDate, 23, 59, 0) : "";

        const params = new URLSearchParams();
        params.set("userId", String(userId));
        if (start_date) params.set("start_date", start_date);
        if (end_date) params.set("end_date", end_date);

        const res = await http.get(`/api/dashboard/getDashboard?${params.toString()}`);
        const data = res?.data?.data;

        if (!alive) return;

        const summary = data?.summary || {};
        setKpi({
          totalKg: Number(summary.total_weight || 0),
          totalMeals: Number(summary.total_meals || 0),
          totalBeneficiaries: Number(summary.total_recipients || 0),
          donors: Number(summary.total_donors || 0),
          donateTimes: Number(summary.total_donation_items || 0),
        });

        // ✅ เอา all_foodtype มาแสดงตาราง/จัดอันดับสี
        const list = Array.isArray(data?.all_foodtype) ? data.all_foodtype : [];
        const mapped = list
          .map((x) => ({
            name: String(x?.food_type_name || ""),
            value: Number(x?.total_weight || 0),
          }))
          .sort((a, b) => Number(b.value || 0) - Number(a.value || 0))
          .map((x, idx) => ({
            ...x,
            // ✅ Top5 ตามรูป, อันดับ 6+ เป็นสีขาวทั้งหมด
            color: idx < 5 ? TOP5_COLORS[idx] : "#FFFFFF",
          }));

        setRows(mapped);
      } catch (e) {
        console.error("GET /api/dashboard/getDashboard error:", e);
        if (!alive) return;
        setRows([]);
        setKpi({
          totalKg: 0,
          totalMeals: 0,
          totalBeneficiaries: 0,
          donors: 0,
          donateTimes: 0,
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [http, range.startDate, range.endDate]);

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex flex-col pb-40">
      <Header />
      <LoadingOverlay show={loading} />

      <div className="px-4 sm:px-6">
        <div className="flex items-start flex-col gap-3 md:flex-row md:justify-between">
          <div className="text-[18px] sm:text-[20px] font-bold text-[#407865]">อาหารบริจาค</div>

          <div className="flex items-center flex-row gap-2">
            {/* <button
              type="button"
              onClick={handleDownloadWeeklyExcel}
              disabled={isDownloading}
              className="bg-[#407865] text-white px-4 py-2 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isDownloading ? "กำลังดาวน์โหลด..." : "Download Excel"}
            </button> */}
            <div className="rounded-xl bg-white border-2 border-[#9CA3AF] px-3 py-2 shadow-sm">
              <ThaiBEDateRangePicker
                modeKey=""
                today={today}
                Icon={CalendarIcon}
                startDate={range.startDate}
                endDate={range.endDate}
                onChange={({ startDate, endDate }) => setRange({ startDate, endDate })}
              />
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KpiCard icon={Card1Icon} color="#407865" title="น้ำหนักอาหารบริจาค" value={`${THB.format(kpi.totalKg)} `} unit="กก." />
          <KpiCard
            icon={Card2Icon}
            title="จำนวนมื้อ"
            value={THB_NO_DECIMALS.format(kpi.totalMeals)}
            unit="มื้อ"
          />

          <KpiCard
            icon={Card3Icon}
            title="จำนวนผู้รับอาหาร"
            value={THB_NO_DECIMALS.format(kpi.totalBeneficiaries)}
            unit="คน"
          />

          <KpiCard
            icon={Card4Icon}
            title="จำนวนผู้บริจาค"
            value={THB_NO_DECIMALS.format(kpi.donors)}
            amount
            unit="ราย"
            subText={`จำนวนที่บริจาค ${THB_NO_DECIMALS.format(kpi.donateTimes)} ครั้ง`}
          />
        </div>

        <div className="mt-4 sm:mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          <div className="lg:col-span-5">
            <Card>
              <div className="text-[15px] font-semibold text-slate-800">ประเภทของอาหารบริจาค</div>

              <div className="mt-3 flex items-center justify-center">
                <div className="h-[300px] w-full max-w-[300px]" onMouseDown={(e) => e.preventDefault()}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={62}
                        outerRadius={90}
                        paddingAngle={2}
                        stroke="transparent"
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((x, i) => (
                          <Cell
                            key={`${x.name}-${i}`}
                            fill={x.color || "#fff"}
                            stroke="transparent"
                            strokeWidth={0}
                          />
                        ))}
                      </Pie>

                      <Tooltip
                        formatter={(value, name) => {
                          const pct = (Number(value || 0) / pieTotal) * 100;
                          return [`${pct.toFixed(1)}%`, name];
                        }}
                      />

                      <text
                        x="50%"
                        y="50%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="fill-[#000]"
                        style={{ fontSize: 18, fontWeight: 700 }}
                      >
                        Top 5
                      </text>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Card>
          </div>

          <div className="lg:col-span-7">
            <Card>
              <div className="mt-3 overflow-hidden rounded-md border border-slate-100">
                <table className="w-full table-fixed text-left text-[12px] sm:text-[13px]">
                  <thead className="bg-[#F3F0DC]">
                    <tr>
                      <th className="w-[60%] px-2 sm:px-4 py-2 font-medium">ประเภทอาหาร</th>
                      <th className="w-[40%] px-2 sm:px-4 py-2 text-right font-medium whitespace-nowrap">
                        ปริมาณ(กิโลกรัม)
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {pagedRows.map((r, idx) => {
                      const isWhite =
                        String(r.color || "").toLowerCase() === "#ffffff" ||
                        String(r.color || "").toLowerCase() === "#fff";

                      return (
                        <tr
                          key={`${r.name}-${(page - 1) * PAGE_SIZE + idx}`}
                          className={idx % 2 ? "bg-slate-50/40" : "bg-white"}
                        >
                          <td className="px-2 sm:px-4 py-2 text-slate-800 align-top">
                            <div className="flex items-start gap-2 min-w-0">
                              <span
                                className="mt-1 h-2.5 w-2.5 rounded-full shrink-0"
                                style={{
                                  backgroundColor: r.color || "#fff",
                                  border: `1px solid ${isWhite ? "#FFFFFF" : "#E2E8F0"}`,
                                }}
                              />

                              {/* ✅ บีบให้พอดีจอ: ไม่ truncate แล้ว + wrap + break คำได้ */}
                              <span className="min-w-0 whitespace-normal break-words leading-4">
                                {r.name}
                              </span>
                            </div>
                          </td>

                          <td className="px-2 sm:px-4 py-2 text-right font-semibold text-slate-900 whitespace-nowrap align-top">
                            {THB.format(r.value)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>


              <div className="mt-3 flex items-center justify-center gap-2 text-[12px] text-slate-600">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="h-7 w-7 grid place-items-center rounded-full border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ‹
                </button>

                <span className="h-7 min-w-[28px] grid place-items-center rounded-md border border-slate-200 bg-white font-semibold">{page}</span>
                <span>of {totalPages}</span>

                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="h-7 w-7 grid place-items-center rounded-full border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ›
                </button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <BottomBar />
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-white ">{children}</div>;
}

function KpiCard({ icon, title, value, unit, subText, amount, color }) {
  const Icon = icon || (() => null);

  return (
    <div className="rounded-2xl bg-white border border-[#9CA3AF] shadow-sm p-4 sm:p-5">
      <div className="flex items-start flex-col gap-3">
        <div className="h-10 w-10 grid place-items-center text-xl">
          <Icon aria-hidden style={{ color: color || "#407865" }} />
        </div>

        {amount ? (
          <div className="min-w-0">
            <div className="text-sm sm:text-md text-[#4B5563] font-medium">
              {title}{" "}
              <span className="text-[20px] sm:text-[22px] font-bold text-slate-900 leading-none inline-block">
                {value}
              </span>
              {!!unit && <span className="text-[12px] text-[#4B5563] ml-1 inline-block">{unit}</span>}
            </div>
            {!!subText && <div className="mt-1 text-[11px] text-[#4B5563]">{subText}</div>}
          </div>
        ) : (
          <div className="min-w-0">
            <div className="text-sm sm:text-md text-[#4B5563] font-medium">{title}</div>
            <div className="mt-1 flex items-end gap-2">
              <div className="text-[20px] sm:text-[22px] font-bold text-slate-900 leading-none">{value}</div>
              {!!unit && <div className="text-[12px] text-[#4B5563] mb-[1px]">{unit}</div>}
            </div>
            {!!subText && <div className="mt-1 text-[11px] text-[#4B5563]">{subText}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
